import { createElement, type JSX, type ComponentType } from 'react';
/**
 * @author Spencer Marx
 * @param hook any hook containing some logic that we want to use in a component, will get Props as an argument
 * @param Component a stateless component that we want to wrap with our logic, will also receive Props
 * @returns A Stateful Component
 * @description This utility enables us to split business logic in the frontend project and
 * simple presentational components in the ui library.
 * @example
 *  const CallControls = withState<StateFromHook, Props>(
 *    useMyHook,
 *    MyStatelessComponent,
 *  );
 */
export const withState = <
  State extends Record<string, unknown>,
  Props extends Record<string, unknown> = Record<string, never>,
>(
  hook: (props: Props) => State,
  Component: ComponentType<State & Props>,
): ((props: Props) => JSX.Element) => {
  const WrappedComponent = (props: Props): JSX.Element => {
    const state = hook(props);
    return createElement(Component, { ...state, ...props });
  };

  const displayName = Component.displayName ?? Component.name;

  WrappedComponent.displayName = `withState(${displayName})`;

  return WrappedComponent;
};
